import { defineEnum, type EnumValueOf } from './define.ts'

/**
 * The enums describing work: what state an assignment is in, how a room came out, and
 * where an export job is in its lifecycle.
 */

/**
 * Assignment lifecycle, shared by an assignment and the report that closes it — the
 * current system uses one enum for both columns and
 * `docs/reference/FUNCTIONALITY.md` requires that "creating a report force-sets the parent
 * assignment's status to the report's status", which only makes sense while they share a
 * value set.
 *
 * `expired` is written by the nightly sweep, not by a worker.
 */
export const assignmentStatus = defineEnum('AssignmentStatus', [
  'not_started',
  'in_progress',
  'partially_completed',
  'completed',
  'not_completed',
  'expired',
])
export type AssignmentStatus = EnumValueOf<typeof assignmentStatus>

/**
 * The three assignment statuses a submitted report can produce.
 *
 * A subset rather than a separate enum: it is the codomain of the status derivation in
 * `../status/derive.ts`, and typing it as such stops a caller from persisting
 * `in_progress` or `expired` as the outcome of a report. `assignmentStatusSchema` still
 * accepts all six, because the assignment column does.
 */
export const REPORT_OUTCOME_STATUSES = [
  'completed',
  'partially_completed',
  'not_completed',
] as const satisfies readonly AssignmentStatus[]
export type ReportOutcomeStatus = (typeof REPORT_OUTCOME_STATUSES)[number]

/**
 * Per-room outcome inside a report. The tri-state derivation in `../status/derive.ts`
 * produces it from the room's per-task completion flags — both levels are recorded now,
 * per `docs/DECISIONS.md` ("Per-task completion — made real").
 */
export const reportRoomStatus = defineEnum('ReportRoomStatus', [
  'not_done',
  'partially_done',
  'done',
])
export type ReportRoomStatus = EnumValueOf<typeof reportRoomStatus>

/**
 * Export-job lifecycle.
 *
 * `waiting → in_progress → completed | failed` is the current system's set. `expired` is
 * added by `docs/DECISIONS.md` ("Export retention — 90 days, R2 lifecycle"): the job row
 * outlives its file, so the history still shows what was exported and when, with the
 * download disabled. It is a distinct state precisely because the current UI's only
 * question — "is it `completed`?" — cannot express "it succeeded and the file is gone".
 */
export const exportJobStatus = defineEnum('ExportJobStatus', [
  'waiting',
  'in_progress',
  'completed',
  'failed',
  'expired',
])
export type ExportJobStatus = EnumValueOf<typeof exportJobStatus>

/**
 * The languages a tenant may enable, and the fallback.
 *
 * `docs/DECISIONS.md` ("Locales — add English, per-tenant default and language set"):
 * four catalogues ship, each tenant picks its default and its enabled set, and **English
 * is the fallback** — replacing the current app's accidental Russian one.
 */
export const locale = defineEnum('Locale', ['en', 'de', 'ru', 'uk'])
export type Locale = EnumValueOf<typeof locale>

/** The fallback locale when a tenant's default is unavailable. */
export const FALLBACK_LOCALE: Locale = 'en'
